import { useLingui } from '@lingui/react/macro';
import { createFileRoute, isNotFound, isRedirect, notFound } from '@tanstack/react-router';
import { Suspense } from 'react';

import { Profile } from '#modules/user/features/profile/components/Profile';
import { ProfileSkeleton } from '#modules/user/features/profile/components/Profile/ProfileSkeleton';
import { getProfileQueryOptions } from '#modules/user/features/profile/model/profile';
import { ApiError } from '#features/api/utils/ApiError';
import { ContentNotFound } from '#features/error/components/ContentNotFound';
import { isContentNotFoundError } from '#features/error/utils/isContentNotFoundError';
import { Link } from '#features/routing';
import { seedCachedOwnProfile } from '#features/routing/utils/nativeCachedRoutes';

export const Route = createFileRoute('/{-$locale}/_layout/_dashboard/_authed/players/$playerId')({
  // ?gameId — игра, из-за которой посетитель сюда пришёл (ссылка с заявки). Профиль
  // открывается сразу на ней, а не на первой попавшейся: человек уже смотрит конкретную игру,
  // и заставлять его выбирать её заново — терять шаг ровно в момент решения.
  //
  // Возвращаем именно необязательный ключ (`{}` вместо `{ gameId: undefined }`): при
  // обязательном ключе роутер требует `search` от каждой ссылки на профиль, а их в проекте
  // десяток — от карточки автора до уведомлений.
  validateSearch: (search: Record<string, unknown>): { gameId?: string } =>
    typeof search.gameId === 'string' ? { gameId: search.gameId } : {},
  loader: async ({ params: { playerId }, context }) => {
    try {
      if (
        import.meta.env.VITE_IS_NATIVE === 'true' &&
        typeof window !== 'undefined' &&
        seedCachedOwnProfile(context.queryClient, playerId)
      ) {
        // The mounted profile still checks the server; opening a saved own profile never waits.
        return null;
      }
      const profile = await context.queryClient.fetchQuery(
        getProfileQueryOptions({ displayNameOrId: playerId })
      );

      if (!profile?.id) {
        throw notFound();
      }

      return null;
    } catch (error) {
      // notFound() thrown above lands here too — let it through to notFoundComponent.
      if (isRedirect(error) || isNotFound(error)) throw error;
      console.error(error);

      // A deleted account still resolves (it renders as "deleted account"), so this only
      // fires when the nickname itself is gone. The gateway reports that as a GraphQL
      // error over HTTP 200, hence the code check rather than the status.
      if (isContentNotFoundError(error) || (error instanceof ApiError && error.status === 404)) {
        throw notFound();
      }

      // Don't crash the route — component will retry via useSuspenseQuery
      return null;
    }
  },
  notFoundComponent: PlayerNotFound,
  /*
    Пауза приходится на сам загрузчик: он ждёт `fetchQuery`, а тот при сетевом сбое повторяет
    попытки. Всё это время маршрут «в пути», и без своего экрана ожидания страница показывала
    пустую колонку между шапкой и подвалом — 15–20 секунд, неотличимых от поломки.
  */
  pendingComponent: ProfileSkeleton,
  /*
    Скелет появляется, только если загрузчик идёт дольше секунды (pendingMs по умолчанию), и
    раз уж появился – держится полсекунды. Общий минимум в router.tsx снят до нуля ради холодного
    старта приложения, а здесь он нужен: профиль, приехавший на 1,05 с, иначе показал бы скелет на
    один кадр и мигнул.
  */
  pendingMinMs: 500,
  component: RouteComponent,
  ssr: false,
});

function PlayerNotFound() {
  const { t } = useLingui();

  return (
    <ContentNotFound
      title={t`notFound.player.title`}
      description={t`notFound.player.description`}
      action={<Link to="/{-$locale}/dashboard/discover">{t`notFound.findTeammates`}</Link>}
    />
  );
}

// Провайдера капчи здесь нет: жалоба на пользователя — единственный её потребитель на этой
// странице, а ReportModal монтирует провайдер сам, пока открыт.
function RouteComponent() {
  const { playerId } = Route.useParams();
  const { gameId } = Route.useSearch();

  /*
    Граница ожидания обязательна: загрузчик намеренно не роняет маршрут (см. выше) и отдаёт
    управление компоненту, а тот приостанавливается на `useSuspenseQuery`. Без границы страница
    показывала пустую колонку между шапкой и подвалом всё время повторов — при сетевом сбое это
    15–20 секунд, неотличимых от сломанной страницы.
  */
  return (
    <Suspense fallback={<ProfileSkeleton />}>
      <Profile playerId={playerId} contextGameId={gameId} />
    </Suspense>
  );
}
